// src/pinball/PinballEngine.ts - Main game engine orchestrating physics, scoring, state

import {
  TableThemeConfig,
  PinballGameState,
  PinballGameStatus,
  BallState,
  CollisionEvent,
  PhysicsBody,
  FlipperBody,
  StaticCollider,
  Vector2D,
  PinballGameResult,
  TableElementConfig,
} from './types';
import { PinballPhysics } from './PinballPhysics';
import { ScoringSystem, ScoreEvent } from './ScoringSystem';

type ScoreChangeCallback = (score: number) => void;
type BallDrainCallback = (ballsRemaining: number) => void;
type GameOverCallback = (finalScore: number) => void;
type CollisionCallback = (event: CollisionEvent) => void;

export class PinballEngine {
  private tableConfig: TableThemeConfig;
  private physics: PinballPhysics;
  private scoring: ScoringSystem;
  private state: PinballGameState;
  private elementMap: Map<string, TableElementConfig>;

  // Callbacks
  private scoreChangeCallbacks: ScoreChangeCallback[] = [];
  private ballDrainCallbacks: BallDrainCallback[] = [];
  private gameOverCallbacks: GameOverCallback[] = [];
  private collisionCallbacks: CollisionCallback[] = [];

  // Tilt detection
  private flipperActivations: number[] = [];
  private tiltWarningTime: number = 0;

  // Drop target tracking
  private dropTargetResetTimers: Map<string, number> = new Map();

  // Performance monitoring
  private frameTimes: number[] = [];
  private currentSubsteps: number;

  constructor(tableConfig: TableThemeConfig) {
    this.tableConfig = tableConfig;
    this.physics = new PinballPhysics();
    this.scoring = new ScoringSystem(tableConfig.scoring);
    this.elementMap = new Map();
    this.currentSubsteps = 2;

    // Build element lookup
    for (const element of tableConfig.elements) {
      this.elementMap.set(element.id, element);
    }

    this.state = this.createInitialState();
  }

  private createInitialState(): PinballGameState {
    return {
      balls: [],
      activeBallCount: 0,
      score: 0,
      scoreMultiplier: 1,
      bonusMultiplier: 1,
      currentBallNumber: 1,
      totalBalls: 3,
      ballsRemaining: 3,
      ballSaveActive: false,
      ballSaveExpiry: 0,
      multiballActive: false,
      tiltWarnings: 0,
      tilted: false,
      dropTargetStates: {},
      laneCompletions: {},
      lastHitTimestamp: 0,
      comboCount: 0,
      leftFlipperActive: false,
      rightFlipperActive: false,
      plungerPower: 0,
      plungerCharging: false,
      ballInLauncher: true,
      status: 'ready',
      isPaused: false,
      gameStartTime: 0,
      elapsedTime: 0,
    };
  }

  // Game lifecycle
  initialize(): void {
    // Set up physics colliders from table config
    const colliders: StaticCollider[] = [];

    // Walls
    for (const wall of this.tableConfig.walls) {
      colliders.push({
        id: wall.id,
        type: 'segment',
        shape: { start: wall.start, end: wall.end },
        restitution: wall.restitution,
        elementRef: wall.id,
      });
    }

    // Table elements
    for (const element of this.tableConfig.elements) {
      if (element.type === 'bumper' || element.type === 'pop_bumper') {
        colliders.push({
          id: `collider_${element.id}`,
          type: 'circle',
          shape: {
            center: element.position,
            radius: element.properties.radius || 15,
          },
          restitution: element.properties.restitution || 0.9,
          elementRef: element.id,
        });
      } else if (element.type === 'target' || element.type === 'drop_target') {
        colliders.push({
          id: `collider_${element.id}`,
          type: 'segment',
          shape: {
            start: {
              x: element.position.x - (element.properties.width || 20) / 2,
              y: element.position.y,
            },
            end: {
              x: element.position.x + (element.properties.width || 20) / 2,
              y: element.position.y,
            },
          },
          restitution: element.properties.restitution || 0.5,
          elementRef: element.id,
        });
      } else if (element.type === 'slingshot') {
        colliders.push({
          id: `collider_${element.id}`,
          type: 'segment',
          shape: {
            start: element.position,
            end: {
              x: element.position.x + (element.properties.width || 40),
              y: element.position.y + (element.properties.height || 40),
            },
          },
          restitution: element.properties.restitution || 0.8,
          elementRef: element.id,
        });
      }
    }

    // Drain collider
    colliders.push({
      id: 'drain_collider',
      type: 'segment',
      shape: {
        start: {
          x: this.tableConfig.drain.position.x - this.tableConfig.drain.width / 2,
          y: this.tableConfig.drain.position.y,
        },
        end: {
          x: this.tableConfig.drain.position.x + this.tableConfig.drain.width / 2,
          y: this.tableConfig.drain.position.y,
        },
      },
      restitution: 0,
      elementRef: 'drain',
    });

    this.physics.setStaticColliders(colliders);

    // Set up flippers
    for (const flipperConfig of this.tableConfig.flippers) {
      const flipperBody: FlipperBody = {
        id: flipperConfig.id,
        pivot: flipperConfig.pivot,
        length: flipperConfig.length,
        width: 8,
        restAngle: flipperConfig.restAngle,
        activeAngle: flipperConfig.activeAngle,
        currentAngle: flipperConfig.restAngle,
        angularVelocity: 0,
        maxAngularVelocity: Math.PI / (flipperConfig.activationTime / 1000),
        side: flipperConfig.side,
        isActive: false,
      };
      this.physics.addFlipper(flipperBody);
    }

    // Initialize drop target states
    for (const element of this.tableConfig.elements) {
      if (element.type === 'drop_target') {
        this.state.dropTargetStates[element.id] = false; // false = up/active
      }
    }

    // Initialize lane completions
    for (const element of this.tableConfig.elements) {
      if (element.type === 'lane' || element.type === 'orbit') {
        this.state.laneCompletions[element.id] = false;
      }
    }
  }

  startGame(): void {
    this.state.status = 'playing';
    this.state.gameStartTime = Date.now();
    this.state.ballInLauncher = true;
    this.serveBall();
  }

  pause(): void {
    if (this.state.status === 'playing') {
      this.state.isPaused = true;
      this.state.status = 'paused';
    }
  }

  resume(): void {
    if (this.state.status === 'paused') {
      this.state.isPaused = false;
      this.state.status = 'playing';
    }
  }

  endGame(): PinballGameResult {
    this.state.status = 'game_over';
    return {
      tableTheme: this.tableConfig.id,
      finalScore: this.state.score,
      ballsUsed: this.state.currentBallNumber,
      duration: Date.now() - this.state.gameStartTime,
      jackpotsHit: 0,
      maxCombo: this.state.comboCount,
      maxMultiplier: this.state.scoreMultiplier,
      completedAt: Date.now(),
    };
  }

  // Game loop update
  update(dt: number): void {
    if (this.state.isPaused || this.state.status !== 'playing') return;

    // Performance monitoring
    const frameStart = performance.now();

    // Update plunger
    if (this.state.plungerCharging) {
      this.state.plungerPower = Math.min(1, this.state.plungerPower + dt / (this.tableConfig.plunger.chargeTime / 1000));
    }

    // Hold ball in launcher position (prevent gravity from pulling it down)
    if (this.state.ballInLauncher) {
      const ball = this.physics.getBodies().find(b => b.isActive);
      if (ball) {
        ball.position = { x: this.tableConfig.plunger.position.x, y: this.tableConfig.plunger.position.y - 20 };
        ball.velocity = { x: 0, y: 0 };
      }
    }

    // Physics step
    const events = this.physics.step(dt);

    // Process collision events
    for (const event of events) {
      this.processCollisionEvent(event);
    }

    // Check ball save expiry
    if (this.state.ballSaveActive && Date.now() > this.state.ballSaveExpiry) {
      this.state.ballSaveActive = false;
    }

    // Check drain
    this.checkDrain();

    // Update drop target reset timers
    this.updateDropTargetTimers();

    // Update combo
    this.scoring.updateCombo(Date.now());
    this.state.comboCount = this.scoring.getComboCount();

    // Update elapsed time
    this.state.elapsedTime = Date.now() - this.state.gameStartTime;

    // Sync state from scoring
    this.state.scoreMultiplier = this.scoring.getMultiplier();
    this.state.bonusMultiplier = this.scoring.getBonusMultiplier();

    // Update ball states
    this.syncBallStates();

    // Performance check
    const frameTime = performance.now() - frameStart;
    this.frameTimes.push(frameTime);
    if (this.frameTimes.length > 10) this.frameTimes.shift();

    // Reduce substeps if consistently over budget
    if (this.frameTimes.length >= 5) {
      const overBudget = this.frameTimes.filter(t => t > 32).length;
      if (overBudget >= 5 && this.currentSubsteps > 1) {
        this.currentSubsteps = 1;
        this.physics.updateConfig({ physicsSubsteps: 1 });
      }
    }
  }

  // Input controls
  activateFlipper(side: 'left' | 'right'): void {
    if (this.state.isPaused || this.state.tilted) return;

    // Tilt detection
    this.flipperActivations.push(Date.now());
    this.flipperActivations = this.flipperActivations.filter(t => Date.now() - t < 1000);

    if (this.flipperActivations.length > 10) {
      if (this.state.tiltWarnings === 0) {
        this.state.tiltWarnings = 1;
        this.tiltWarningTime = Date.now();
      } else if (Date.now() - this.tiltWarningTime < 3000) {
        // Second violation within 3 seconds - TILT
        this.state.tilted = true;
        this.state.tiltWarnings = 2;
        // Disable flippers
        for (const flipperConfig of this.tableConfig.flippers) {
          this.physics.setFlipperState(flipperConfig.id, false);
        }
        return;
      }
    }

    const flipperId = this.tableConfig.flippers.find(f => f.side === side)?.id;
    if (flipperId) {
      this.physics.setFlipperState(flipperId, true);
    }

    if (side === 'left') {
      this.state.leftFlipperActive = true;
    } else {
      this.state.rightFlipperActive = true;
    }
  }

  releaseFlipper(side: 'left' | 'right'): void {
    const flipperId = this.tableConfig.flippers.find(f => f.side === side)?.id;
    if (flipperId) {
      this.physics.setFlipperState(flipperId, false);
    }

    if (side === 'left') {
      this.state.leftFlipperActive = false;
    } else {
      this.state.rightFlipperActive = false;
    }
  }

  chargePlunger(): void {
    if (this.state.ballInLauncher && !this.state.isPaused) {
      this.state.plungerCharging = true;
      this.state.plungerPower = 0;
    }
  }

  releasePlunger(): void {
    if (this.state.plungerCharging && this.state.ballInLauncher) {
      const power = Math.max(0.1, this.state.plungerPower);
      const launchSpeed = power * this.tableConfig.plunger.maxLaunchSpeed;

      // Find the ball in launcher and launch it
      const ball = this.physics.getBodies().find(b => b.isActive);
      if (ball) {
        ball.velocity.y = -launchSpeed;
        this.state.ballInLauncher = false;

        // Activate ball save
        this.state.ballSaveActive = true;
        this.state.ballSaveExpiry = Date.now() + 10000;
      }

      this.state.plungerCharging = false;
      this.state.plungerPower = 0;
    }
  }

  // State
  getState(): PinballGameState {
    return { ...this.state };
  }

  getPhysics() {
    return this.physics;
  }

  getPlungerPosition() {
    return this.tableConfig.plunger.position;
  }

  setBallInLauncher(value: boolean) {
    this.state.ballInLauncher = value;
  }

  serialize(): string {
    return JSON.stringify(this.state);
  }

  static deserialize(json: string): PinballGameState {
    try {
      const parsed = JSON.parse(json);
      // Validate essential fields
      if (typeof parsed.score !== 'number' || typeof parsed.status !== 'string') {
        throw new Error('Invalid game state: missing required fields');
      }
      return parsed as PinballGameState;
    } catch (e) {
      throw new Error(`Failed to deserialize game state: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  }

  // Event callbacks
  onScoreChange(callback: ScoreChangeCallback): void {
    this.scoreChangeCallbacks.push(callback);
  }

  onBallDrain(callback: BallDrainCallback): void {
    this.ballDrainCallbacks.push(callback);
  }

  onGameOver(callback: GameOverCallback): void {
    this.gameOverCallbacks.push(callback);
  }

  onCollision(callback: CollisionCallback): void {
    this.collisionCallbacks.push(callback);
  }

  // Private methods
  private serveBall(): void {
    const ballId = `ball_${Date.now()}`;
    const plungerPos = this.tableConfig.plunger.position;

    const body: PhysicsBody = {
      id: ballId,
      position: { x: plungerPos.x, y: plungerPos.y - 20 },
      velocity: { x: 0, y: 0 },
      radius: 10,
      mass: 1,
      isActive: true,
    };

    this.physics.addBody(body);
    this.state.balls.push({
      id: ballId,
      position: { ...body.position },
      velocity: { x: 0, y: 0 },
      isActive: true,
    });
    this.state.activeBallCount = 1;
    this.state.ballInLauncher = true;
  }

  private checkDrain(): void {
    const drainY = this.tableConfig.drain.position.y;
    const bodies = this.physics.getBodies();

    for (const body of bodies) {
      if (!body.isActive) continue;

      if (body.position.y > drainY + 20) {
        this.handleBallDrain(body);
      }
    }
  }

  private handleBallDrain(body: PhysicsBody): void {
    // Ball save check
    if (this.state.ballSaveActive && !this.state.multiballActive) {
      this.state.ballSaveActive = false;
      // Return ball to launcher
      body.position = { ...this.tableConfig.plunger.position, y: this.tableConfig.plunger.position.y - 20 };
      body.velocity = { x: 0, y: 0 };
      this.state.ballInLauncher = true;
      return;
    }

    // Deactivate ball
    body.isActive = false;
    this.state.activeBallCount--;

    // During multiball, don't decrement ball count until last ball
    if (this.state.multiballActive) {
      if (this.state.activeBallCount <= 1) {
        this.state.multiballActive = false;
      }
      return;
    }

    // Calculate end-of-ball bonus
    if (!this.state.tilted) {
      const bonus = this.scoring.calculateEndOfBallBonus(this.state);
      this.state.score += bonus;
      this.emitScoreChange();
    }

    // Reset tilt
    this.state.tilted = false;
    this.state.tiltWarnings = 0;
    this.flipperActivations = [];

    this.state.ballsRemaining--;
    this.emitBallDrain();

    if (this.state.ballsRemaining <= 0) {
      // Game over
      this.state.status = 'game_over';
      this.emitGameOver();
    } else {
      // Serve next ball
      this.state.currentBallNumber++;
      this.scoring.resetForNewBall();
      this.state.laneCompletions = {};
      for (const element of this.tableConfig.elements) {
        if (element.type === 'lane' || element.type === 'orbit') {
          this.state.laneCompletions[element.id] = false;
        }
      }
      this.serveBall();
    }
  }

  private processCollisionEvent(event: CollisionEvent): void {
    // Emit to listeners
    for (const cb of this.collisionCallbacks) {
      cb(event);
    }

    // Find element
    const element = this.elementMap.get(event.elementId);
    if (!element) return;

    // Skip if drop target is already down
    if (element.type === 'drop_target' && this.state.dropTargetStates[element.id]) {
      return;
    }

    // Process scoring
    const scoreEvent = this.scoring.processHit(element, this.state);
    this.state.score += scoreEvent.multipliedPoints;
    this.state.lastHitTimestamp = Date.now();
    this.emitScoreChange();

    // Handle specific element types
    switch (element.type) {
      case 'drop_target':
        this.handleDropTargetHit(element);
        break;
      case 'lane':
      case 'orbit':
        this.state.laneCompletions[element.id] = true;
        this.checkLaneCompletion();
        break;
      case 'pop_bumper':
        this.applyPopBumperImpulse(event);
        break;
      case 'slingshot':
        this.applySlingshot(event);
        break;
      default:
        break;
    }

    // Check jackpot condition
    this.checkJackpotCondition();

    // Check multiball condition
    this.checkMultiballCondition();
  }

  private handleDropTargetHit(element: TableElementConfig): void {
    this.state.dropTargetStates[element.id] = true;

    // Check if bank is complete
    if (element.properties.group) {
      const groupElements = this.tableConfig.elements.filter(
        e => e.type === 'drop_target' && e.properties.group === element.properties.group
      );
      const allDown = groupElements.every(e => this.state.dropTargetStates[e.id]);

      if (allDown) {
        // Bank complete - increase multiplier
        this.scoring.increaseScoreMultiplier(1);
        this.state.scoreMultiplier = this.scoring.getMultiplier();

        // Schedule reset after 3 seconds
        const groupId = element.properties.group;
        this.dropTargetResetTimers.set(groupId, Date.now() + 3000);
      }
    }
  }

  private updateDropTargetTimers(): void {
    const now = Date.now();
    for (const [groupId, resetTime] of this.dropTargetResetTimers) {
      if (now >= resetTime) {
        // Reset all targets in this group
        const groupElements = this.tableConfig.elements.filter(
          e => e.type === 'drop_target' && e.properties.group === groupId
        );
        for (const e of groupElements) {
          this.state.dropTargetStates[e.id] = false;
        }
        this.dropTargetResetTimers.delete(groupId);
      }
    }
  }

  private applyPopBumperImpulse(event: CollisionEvent): void {
    const body = this.physics.getBody(event.bodyId);
    if (!body) return;

    const impulse = 300; // Active impulse
    body.velocity.x += event.contactNormal.x * impulse;
    body.velocity.y += event.contactNormal.y * impulse;
  }

  private applySlingshot(event: CollisionEvent): void {
    const body = this.physics.getBody(event.bodyId);
    if (!body) return;

    // Lateral impulse
    const lateralImpulse = 200;
    body.velocity.x += event.contactNormal.x * lateralImpulse;
  }

  private checkLaneCompletion(): void {
    const lanes = this.tableConfig.elements.filter(e => e.type === 'lane');
    const allComplete = lanes.every(l => this.state.laneCompletions[l.id]);
    if (allComplete && lanes.length > 0) {
      this.scoring.increaseBonusMultiplier(1);
      // Reset lane completions
      for (const lane of lanes) {
        this.state.laneCompletions[lane.id] = false;
      }
    }
  }

  private checkJackpotCondition(): void {
    const condition = this.tableConfig.jackpotCondition;
    const allMet = condition.requiredElements.every(id => {
      const element = this.elementMap.get(id);
      if (!element) return false;
      if (element.type === 'drop_target') {
        return this.state.dropTargetStates[id];
      }
      if (element.type === 'lane' || element.type === 'orbit') {
        return this.state.laneCompletions[id];
      }
      return false;
    });

    if (allMet && condition.requiredElements.length > 0) {
      const jackpotEvent = this.scoring.awardJackpot();
      this.state.score += jackpotEvent.multipliedPoints;
      this.emitScoreChange();
    }
  }

  private checkMultiballCondition(): void {
    if (this.state.multiballActive) return;

    const condition = this.tableConfig.multiballCondition;
    const allMet = condition.requiredElements.every(id => {
      const element = this.elementMap.get(id);
      if (!element) return false;
      if (element.type === 'drop_target') {
        return this.state.dropTargetStates[id];
      }
      return this.state.laneCompletions[id] || false;
    });

    if (allMet && condition.requiredElements.length > 0) {
      this.activateMultiball(condition.ballCount);
    }
  }

  private activateMultiball(ballCount: number): void {
    this.state.multiballActive = true;

    // Launch additional balls
    for (let i = 1; i < ballCount; i++) {
      const ballId = `ball_multi_${Date.now()}_${i}`;
      const body: PhysicsBody = {
        id: ballId,
        position: {
          x: this.tableConfig.plunger.position.x,
          y: this.tableConfig.plunger.position.y - 40 * i,
        },
        velocity: { x: (Math.random() - 0.5) * 100, y: -500 },
        radius: 10,
        mass: 1,
        isActive: true,
      };
      this.physics.addBody(body);
      this.state.balls.push({
        id: ballId,
        position: { ...body.position },
        velocity: { ...body.velocity },
        isActive: true,
      });
    }
    this.state.activeBallCount = ballCount;
  }

  private syncBallStates(): void {
    const bodies = this.physics.getBodies();
    this.state.balls = bodies.map(b => ({
      id: b.id,
      position: { ...b.position },
      velocity: { ...b.velocity },
      isActive: b.isActive,
    }));
    this.state.activeBallCount = bodies.filter(b => b.isActive).length;
  }

  private emitScoreChange(): void {
    for (const cb of this.scoreChangeCallbacks) {
      cb(this.state.score);
    }
  }

  private emitBallDrain(): void {
    for (const cb of this.ballDrainCallbacks) {
      cb(this.state.ballsRemaining);
    }
  }

  private emitGameOver(): void {
    for (const cb of this.gameOverCallbacks) {
      cb(this.state.score);
    }
  }

  // Handle NaN/Infinity detection
  private sanitizeBody(body: PhysicsBody): void {
    if (!isFinite(body.position.x) || !isFinite(body.position.y)) {
      body.position = { ...this.tableConfig.plunger.position, y: this.tableConfig.plunger.position.y - 20 };
      body.velocity = { x: 0, y: 0 };
    }
    if (!isFinite(body.velocity.x) || !isFinite(body.velocity.y)) {
      body.velocity = { x: 0, y: 0 };
    }
  }
}
